import React, { PureComponent, Fragment } from 'react'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { t } from 'translation'

import PlayerControl from './PlayerControl'
import PlayerDurationSlider from './PlayerDurationSlider'
import PlayerImmersiveVideo from 'components/VideoPlayer/VideoPlayer'
import PlayerPlaylistNextSong from './PlayerPlaylistNextSong'

import {
  setPlayState,
  setSessionId,
  downloadSongs,
  addSongToHistory,
  toggleDownloadInBackground,
  setSessionOnline,
} from 'store/actions'
import { playerSessionLogic } from './scripts'
import { getNativeLocalPath } from 'utils/conversion'
import history from 'utils/appHistory'
import { removeFile, initializeBlobAudio } from 'utils/cordovaFs'
import { SLEEP, ANXIETY, PAIN, AWAKENING } from 'utils/constants'
import { insertSession, getLastId } from 'utils/sqlite'

import { sessionApi } from 'api'

class Player extends PureComponent {
  state = {
    canPlay: false,
    playTimeCurrent: 0,
    selectedDuration:
      (this.props.indicationType === SLEEP || this.props.indicationType === AWAKENING
        ? 15
        : 20) * 60,
    actualDuration: null,
    disableDurationSlider: false,
    blob: null,
  }

  // Instance properties
  audioPlayer1 = null

  audioPlayer2 = null

  eventListenersToRemove = []

  immersiveVideoElement = null

  initialDuration = this.state.selectedDuration

  playPauseBtn = null

  progressBar = null

  pauseUnpauseInterval = 2000

  playerIsReady = false

  // Ref attachment
  attachPlayPauseBtnRef = (btn) => {
    this.playPauseBtn = btn
    this.props.attachPlayPauseBtnKeyRef(btn)
  }

  attachImmersiveVideoRef = (videoEl) => (this.immersiveVideoElement = videoEl)

  // Methods and Handlers
  changeDurationHandler = (newDurationMinutes) => {
    this.setState({ selectedDuration: newDurationMinutes * 60 })
  }

  closeImmersiveVideoHandler = (e) => {
    if (e) e.stopPropagation()
    this.immersiveVideoElement.pause()
    this.props.closeImmersiveVideoHandler()
    this.immersiveVideoElement = null
  }

  getSelectedDuration = () => this.state.selectedDuration

  onFinishSessionHandler = () => {
    if (this.props.playState) this.props.setPlayState(false)
    this.props.onFinishSessionHandler(this.state.playTimeCurrent)
  }

  playImmersiveVideoHandler = () => {
    if (this.immersiveVideoElement) this.immersiveVideoElement.play()
  }

  volumeHandler = async () => {
    this.audioPlayer1.volume = this.props.volume
    this.audioPlayer2.volume = this.props.volume
  }

  togglePlayPauseHandler = async (player, fadePlayer) => {
    this.playerIsReady = !this.props.playState
    // Send session api call when begin
    if (!this.state.disableDurationSlider) {
      const playMode =
        typeof this.props.playingPlaylistSong === 'number'
          ? 'playlist'
          : this.props.isLaunchingAlarm
          ? 'alarm'
          : 'manual'

      const data = {
        playMode,
        songId: this.props.songInfo.songId,
        indication: this.props.indicationType,
        sessionStart: new Date(),
        selectedDuration: this.state.selectedDuration,
      }

      if (this.props.painLevelBefore) data.painBefore = this.props.painLevelBefore

      if (this.props.isOnline) {
        sessionApi
          .sendSessionInfoBefore(data)
          .then(
            ({ result }) =>
              result?.session?.id && this.props.setSessionId(result.session.id)
          )
          .catch((err) => console.log('Send before session error: ', err))

        await this.props.setSessionOnline(true)
      } else {
        data.idempotencyKey = uuidv4()
        await insertSession(this.props.sessionDb, this.props.sessionDbTable, data)

        const sessionId = await getLastId(this.props.sessionDb)

        await this.props.setSessionId(sessionId)
        await this.props.setSessionOnline(false)
      }

      this.setState({
        // Once playing started, disable slider for remainder of session
        disableDurationSlider: true,
      })
    }

    // Every crossover loop event, the players associated
    // with player and fadePlayer will be switched,
    // so pass them as parameters to handler
    const playing = this.props.playState
    // If pausing during a fade, will pause both players,
    // otherwise just need to pause main player
    if (playing) {
      if (
        this.state.playTimeCurrent + this.pauseUnpauseInterval / 1000 <
        this.state.actualDuration
      )
        setTimeout(() => (player.pause(), fadePlayer.pause()), this.pauseUnpauseInterval)
    }

    if (!playing) player.play()

    this.props.setPlayState(!playing)
  }

  updateDisplayedDurationHandler = (actualDuration) => this.setState({ actualDuration })

  updateDisplayedPlayTimeHandler = (playTime) => {
    const flooredTime = Math.floor(playTime)
    if (flooredTime !== this.state.playTimeCurrent) {
      const isDemoMode =
        this.props.isDoctor &&
        flooredTime >= 30 &&
        this.props.demoMode &&
        !this.props.subscribed
      if (isDemoMode) return history.push(`/welcome-doctor`)

      if (this.state.playTimeCurrent === 1) this.props.addSongToHistory()

      this.setState({ playTimeCurrent: flooredTime })
      this.props.setElapsedPlayTime(flooredTime)
    }
  }

  onCanPlayHandler = () => {
    this.setState({ canPlay: true })
  }

  getAudioPath = async () => {
    // On browser, do not use downloaded audio files
    if (!window.cordova) return this.props.songInfo.audioUrl

    const songId = this.props.songInfo.songId
    const platform = window.device?.platform
    const downloadedSong = this.props.downloadedSongs.includes(songId)

    const isIos = window.cordova && window.device?.platform === 'iOS'

    // If on android or iOS and downloaded file, use local version
    if (downloadedSong)
      return getNativeLocalPath(
        this.props.isPro || isIos ? `songs/song_${songId}.mp3` : `songs/song_${songId}.mc`
      )

    if (
      platform === 'Android' &&
      // TODO: In case version refers to API level, check what version refers to exactly
      (window.device?.version.startsWith('11') || window.device?.version.startsWith('30'))
    ) {
      if (
        !this.props.isDownloading ||
        (this.props.downloadPromise && this.props.downloadId === songId)
      ) {
        const downloadSongPromise = this.props.isDownloading
          ? this.props.downloadPromise
          : this.props.downloadSongs([songId], this.props.allSongsMap)
        if (!this.props.downloadInBackground) this.props.toggleDownloadInBackground()

        await downloadSongPromise
      }

      if (this.props.downloadedSongs.includes(songId))
        return getNativeLocalPath(
          this.props.isPro || isIos
            ? `songs/song_${songId}.mp3`
            : `songs/song_${songId}.mc`
        )
    }

    return this.props.songInfo.audioUrl
  }

  initializePlayers = async () => {
    const audioPath = await this.getAudioPath()
    const songId = this.props.songInfo.songId

    if (
      window?.cordova &&
      window.device?.platform === 'Android' &&
      !this.props.isPro &&
      this.props.downloadedSongs.includes(songId)
    ) {
      const blobUrl = await initializeBlobAudio(
        audioPath,
        `${audioPath}e`,
        `songs/song_${songId}.mce`
      )

      this.setState({ blob: blobUrl })
      this.audioPlayer1 = new Audio(blobUrl)
      this.audioPlayer2 = new Audio(blobUrl)
    } else {
      this.audioPlayer1 = new Audio(audioPath)
      this.audioPlayer2 = new Audio(audioPath)
    }

    // On iOS players wont launch until do a load call
    const isNativeIOS =
      /iPad|iPhone|iPod/.test(window?.navigator.userAgent) &&
      !window.MSStream &&
      !window?.cordova
    const platform = window.device?.platform
    if (platform === 'iOS' || isNativeIOS) {
      this.audioPlayer1.load()
      this.audioPlayer2.load()
    }

    const playingPlaylistSong = typeof this.props.playingPlaylistSong === 'number'

    // Initiate algorithm that will run the play session and looping logic
    playerSessionLogic({
      songInfo: this.props.songInfo,
      indicationType: this.props.indicationType,
      getSelectedDuration: this.getSelectedDuration,
      player: this.audioPlayer1,
      fadePlayer: this.audioPlayer2,
      playPauseBtn: this.playPauseBtn,
      togglePlayPause: this.togglePlayPauseHandler,
      volume: this.props.volume,
      updateDisplayedPlayTime: this.updateDisplayedPlayTimeHandler,
      updateDisplayedDuration: this.updateDisplayedDurationHandler,
      onFinishSession: this.onFinishSessionHandler,
      eventListenersToRemove: this.eventListenersToRemove,
      isLaunchingAutomatically: this.props.isLaunchingAlarm || playingPlaylistSong,
      onCanPlayHandler: this.onCanPlayHandler,
      pauseUnpauseInterval: this.pauseUnpauseInterval,
    })

    if (window.cordova && window.device?.platform === 'Android')
      window.cordova.plugins.foregroundService.start(
        t('notification.TITLE'),
        t('notification.DESCRIPTION'),
        'drawable-ldpi-icon'
      )

    if (
      window?.cordova &&
      window.device?.platform === 'Android' &&
      !this.props.isPro &&
      this.props.downloadedSongs.includes(songId)
    )
      removeFile(`song_${this.props.songInfo.songId}.mce`)
  }

  // Lifecycle hooks
  componentDidMount() {
    this.initializePlayers()
  }

  componentDidUpdate() {
    // Launch immersive video if loaded by user
    if (
      this.props.immersiveVideo &&
      this.immersiveVideoElement &&
      this.immersiveVideoElement.paused
    ) {
      this.playImmersiveVideoHandler()
    }

    if (
      this.props.immersiveVideo &&
      this.immersiveVideoElement &&
      !this.immersiveVideoElement.paused &&
      !this.props.playState &&
      this.state.canPlay &&
      !this.playerIsReady
    ) {
      // If launching an immersive video, start player session if it has not started or is paused
      this.playPauseBtn.click()
    }

    // TODO: change to === in prod
    if (this.state.playTimeCurrent >= 60) this.props.setSixtySecondsPlayedHandler()
  }

  componentWillUnmount() {
    this.audioPlayer1?.pause?.()
    this.audioPlayer2?.pause?.()

    this.eventListenersToRemove.forEach(({ target, event, fn }) => {
      if (Array.isArray(fn))
        return fn.forEach((oneFn) => target.removeEventListener(event, oneFn))

      target.removeEventListener(event, fn)
    })

    if (this.audioPlayer1) this.audioPlayer1.src = ''
    if (this.audioPlayer2) this.audioPlayer2.src = ''

    if (window.cordova && window.device?.platform === 'Android')
      window.cordova.plugins.foregroundService.stop()

    if (
      window?.cordova &&
      window.device?.platform === 'Android' &&
      !this.props.isPro &&
      this.props.downloadedSongs.includes(this.props.songInfo.songId)
    ) {
      window.URL.revokeObjectURL(this.state.blob)
      this.setState({ blob: null })
    }
  }

  render() {
    const isPlayingPlaylist = typeof this.props.playingPlaylistSong === 'number'
    const currentSongRemainingSec =
      (this.state.actualDuration || this.state.selectedDuration) -
      this.state.playTimeCurrent

    return (
      <div className="player_wrap">
        <div className="player_col">
          <PlayerControl
            attachPlayPauseBtnRef={this.attachPlayPauseBtnRef}
            playing={this.props.playState}
            playTimeCurrent={this.state.playTimeCurrent}
            songInfo={this.props.songInfo}
            totalTimeDuration={this.state.actualDuration}
            canPlay={this.state.canPlay}
            currentSongRemainingSec={currentSongRemainingSec}
            selectedDuration={this.state.selectedDuration}
            changeDurationHandler={this.changeDurationHandler}
            initialValue={this.initialDuration}
            isPlayingPlaylist={isPlayingPlaylist}
            content={this.props.asidesContent}
            disableDurationSlider={
              this.props.playState || this.state.disableDurationSlider
            }
          />
          {this.props.immersiveVideo && (
            <PlayerImmersiveVideo
              closeImmersiveVideoHandler={this.closeImmersiveVideoHandler}
              setVideoRef={this.attachImmersiveVideoRef}
              videoUrl={this.props.immersiveVideo}
              attachCloseImmersiveBtnRef={this.props.attachCloseImmersiveBtnRef}
              loop
              muted
            />
          )}
        </div>
      </div>
      // <>
      // {isPlayingPlaylist ? (
      //   <PlayerPlaylistNextSong currentSongRemainingSec={currentSongRemainingSec} />
      // ) : (
      //   <PlayerDurationSlider
      //     disabled={
      //       this.props.playState ||
      //       this.state.disableDurationSlider ||
      //       this.props.disableByKey
      //     }
      //     changeDurationHandler={this.changeDurationHandler}
      //     initialValue={this.initialDuration}
      //     selectedDuration={this.state.selectedDuration}
      //   />
      // )}
      //   <PlayerControl
      //     attachPlayPauseBtnRef={this.attachPlayPauseBtnRef}
      //     playing={this.props.playState}
      //     playTimeCurrent={this.state.playTimeCurrent}
      //     songInfo={this.props.songInfo}
      //     totalTimeDuration={this.state.actualDuration}
      //     canPlay={this.state.canPlay}
      //   />
      //   {this.props.immersiveVideo && (
      //     <PlayerImmersiveVideo
      //       closeImmersiveVideoHandler={this.closeImmersiveVideoHandler}
      //       setVideoRef={this.attachImmersiveVideoRef}
      //       videoUrl={this.props.immersiveVideo}
      //       attachCloseImmersiveBtnRef={this.props.attachCloseImmersiveBtnRef}
      //       loop
      //       muted
      //     />
      //   )}
      // </>
    )
  }
}

const mapStateToProps = ({
  auth: { isDoctor, attributes, isPro, isOnline },
  player: {
    allSongsMap,
    immersiveVideo,
    playState,
    volume,
    indicationType,
    songInfo,
    playingPlaylistSong,
    painLevelBefore,
    demoMode,
    sessionDb,
    sessionDbTable,
  },
  download: {
    downloadPromise,
    downloadedSongs,
    isDownloading,
    downloadId,
    downloadInBackground,
  },
  alarm: { isLaunchingAlarm },
}) => ({
  allSongsMap,
  attributes,
  demoMode,
  downloadedSongs,
  downloadId,
  downloadInBackground,
  downloadPromise,
  freeDays: attributes.licenses.freeDays,
  immersiveVideo,
  indicationType,
  isDoctor,
  isPro,
  isDownloading,
  isLaunchingAlarm,
  painLevelBefore,
  playingPlaylistSong,
  playState,
  volume,
  songInfo,
  userCreatedDate: new Date(attributes.userCreatedDate),
  subscribed: JSON.parse(attributes?.subscribed || false),
  isOnline,
  sessionDb,
  sessionDbTable,
})

const willMapDispatchToProps = {
  setSessionId,
  setSessionOnline,
  setPlayState,
  downloadSongs,
  toggleDownloadInBackground,
  addSongToHistory,
}

export default connect(mapStateToProps, willMapDispatchToProps)(Player)

Player.propTypes = {
  immersiveVideo: PropTypes.string,
  isLaunchingAlarm: PropTypes.number,
  disableByKey: PropTypes.bool.isRequired,
  attachPlayPauseBtnKeyRef: PropTypes.func.isRequired,
  isDoctor: PropTypes.bool.isRequired,
  playState: PropTypes.bool.isRequired,
  demoMode: PropTypes.bool.isRequired,
  songInfo: PropTypes.object.isRequired,
  indicationType: PropTypes.oneOf([SLEEP, ANXIETY, PAIN, AWAKENING, null]),
  downloadedSongs: PropTypes.array.isRequired,
  playingPlaylistSong: PropTypes.number,
  closeImmersiveVideoHandler: PropTypes.func.isRequired,
  attachCloseImmersiveBtnRef: PropTypes.func.isRequired,
  onFinishSessionHandler: PropTypes.func.isRequired,
  setSixtySecondsPlayedHandler: PropTypes.func.isRequired,
  setPlayState: PropTypes.func.isRequired,
  setElapsedPlayTime: PropTypes.func.isRequired,
  isDownloading: PropTypes.bool.isRequired,
}

Player.defaultProps = {
  immersiveVideo: null,
  indicationType: null,
  isLaunchingAlarm: null,
  playingPlaylistSong: null,
}
